import type { Blockchain } from "@rarible/api-client"
import { apechainConfig } from "./chains/apechain"
import { arenazConfig } from "./chains/arenaz"
import { baseConfig } from "./chains/base"
import { basecampConfig } from "./chains/basecamp"
import { blockchainTestKekConfig } from "./chains/blockchain-test-kek"
import { electroneumConfig } from "./chains/electroneum"
import { ethereumConfig } from "./chains/ethereum"
import { etherlinkConfig } from "./chains/etherlink"
import { fraxtalConfig } from "./chains/fraxtal"
import { hederaEvmConfig } from "./chains/hederaevm"
import { hyperEvmConfig } from "./chains/hyper-evm"
import { injectiveConfig } from "./chains/injective"
import { injectiveTestnetConfig } from "./chains/injective-testnet"
import { lightlinkConfig } from "./chains/lightlink"
import { matchainConfig } from "./chains/matchain"
import { megaEthTestnetConfig } from "./chains/mega-eth-testnet"
import { megaEthTestnetV2Config } from "./chains/mega-eth-testnet-v2"
import { megaEthConfig } from "./chains/megaeth"
import { moonbeamConfig } from "./chains/moonbeam"
import { polygonConfig } from "./chains/polygon"
import { rariConfig } from "./chains/rari"
import { rariTestnetConfig } from "./chains/rari-testnet"
import { robinhoodConfig } from "./chains/robinhood"
import { sepoliaConfig } from "./chains/sepolia"
import { shapeConfig } from "./chains/shape"
import { somniaConfig } from "./chains/somnia"
import { telosConfig } from "./chains/telos"
import { zilliqaConfig } from "./chains/zilliqa"
import { zkcandyConfig } from "./chains/zkcandy"
import type { StaticBlockchainConfig } from "./types"

// Re-export types and constants
export type {
  StaticBlockchainConfig,
  StaticEclipseBlockchainConfig,
  StaticEthereumBlockchainConfig,
  StaticSolanaBlockchainConfig,
} from "./types"
export {
  FEE_CONFIG_URL,
  UNION_API_MAINNET,
  UNION_API_TESTNET,
} from "./constants"

// Re-export chain configs
export {
  apechainConfig,
  arenazConfig,
  baseConfig,
  basecampConfig,
  blockchainTestKekConfig,
  electroneumConfig,
  ethereumConfig,
  etherlinkConfig,
  fraxtalConfig,
  hederaEvmConfig,
  hyperEvmConfig,
  injectiveConfig,
  injectiveTestnetConfig,
  lightlinkConfig,
  matchainConfig,
  megaEthConfig,
  megaEthTestnetConfig,
  megaEthTestnetV2Config,
  moonbeamConfig,
  polygonConfig,
  rariConfig,
  rariTestnetConfig,
  robinhoodConfig,
  sepoliaConfig,
  shapeConfig,
  somniaConfig,
  telosConfig,
  zilliqaConfig,
  zkcandyConfig,
}
export {
  eclipseMainnetConfig,
  eclipseTestnetConfig,
  getEclipseConfig,
} from "./chains/eclipse"
export {
  getSolanaConfig,
  SOLANA_MARKETPLACE_PROGRAM_PLACEHOLDER,
  solanaDevnetConfig,
  solanaMainnetConfig,
} from "./chains/solana"

/**
 * Testnet chain configs (used when HOST_ENVIRONMENT=testnet)
 */
export const staticTestnetConfigs: StaticBlockchainConfig[] = [
  rariTestnetConfig,
  sepoliaConfig,
]

/**
 * All mainnet chain configs (includes some testnet chains that run on mainnet env)
 */
export const staticMainnetConfigs: StaticBlockchainConfig[] = [
  baseConfig,
  ethereumConfig,
  robinhoodConfig,
  basecampConfig,
  rariConfig,
  somniaConfig,
  arenazConfig,
  injectiveConfig,
  hyperEvmConfig,
  lightlinkConfig,
  electroneumConfig,
  moonbeamConfig,
  apechainConfig,
  polygonConfig,
  zkcandyConfig,
  etherlinkConfig,
  zilliqaConfig,
  shapeConfig,
  matchainConfig,
  telosConfig,
  fraxtalConfig,
  hederaEvmConfig,
  megaEthConfig,
  megaEthTestnetConfig,
  injectiveTestnetConfig,
  blockchainTestKekConfig,
  megaEthTestnetV2Config,
]

/**
 * All chain configs (mainnet MegaETH excluded from arrays since it's newer)
 */
export const allStaticConfigs: StaticBlockchainConfig[] = [
  ...staticMainnetConfigs,
  ...staticTestnetConfigs,
]

/**
 * Lookup a static chain config by Blockchain enum value.
 * Searches all configs (mainnet + testnet).
 */
export function getStaticConfigByBlockchain(
  blockchain: Blockchain,
): StaticBlockchainConfig | undefined {
  return allStaticConfigs.find((c) => c.blockchain === blockchain)
}
